import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Box, Button, Tab, Tabs, useTheme } from "@mui/material";
import { FileDownloadOutlined, ViewColumnOutlined } from "@mui/icons-material";
import {
  Topbar,
  useDispatchOnParams,
  toast,
  ReactTable,
  TableColumn,
  DeleteConfirmationModal,
  FilterButton,
  utils
} from "@vilocnv/allsetra-core";
import { useNavigate, useSearchParams } from "react-router-dom";

// Data
import {
  useAppDispatch,
  useAppSelector,
  useColumnVisibility,
  useObjectsResolvedAddressess
} from "hooks";
import {
  ALL_OBJECTS_TABLE_COLUMNS,
  OBJECTS_COLUMN_VISIBILITY_STORAGE_KEY,
  OBJECTS_PROTECTED_COLUMN_IDS,
  ObjectsTableColumnId
} from "app/data/constants";
import {
  deleteObjectThunk,
  exportObjectsCsvThunk,
  getAllAccountGroupsThunk,
  getAllKeysByAccountThunk,
  getObjectsByQueryThunk,
  getObjectTypesByAccountThunk,
  removeObjectFromLists,
  resetAllObjects,
  setActiveObjectId,
  setObjectActiveTabIndex,
  setShowInactiveObjects,
  setSpecificObject
} from "app/features";
import {
  selectAccountGroups,
  selectAccountKeysState,
  selectDrawerSelectedAccountId,
  selectObjectTypesState,
  selectQueriedObjectsState
} from "app/data/selectors";

import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "app/integrations/microsoftInsights";
import { useTranslation } from "react-i18next";
import { FilterIcon } from "assets/icons";
import { FormikHelpers } from "formik";
import { isEmpty } from "lodash";
import { MainWrapperBox, ObjectTableBox, ScrollableBox } from "./Object.styled";
import {
  applyObjectsFiltersToSearchParams,
  buildActiveStatusWhereClauses,
  buildObjectsFilterWhereClauses,
  countActiveObjectFilters,
  normalizeResponseHeaderValue,
  parseFileNameFromContentDisposition,
  parseObjectsFiltersFromSearchParams,
  triggerBlobDownload
} from "app/data/helpers";
import AccountMaintenanceAlertsSection from "components/sections/objects/AccountMaintenanceAlertsSection/AccountMaintenanceAlertsSection";
import useMapPreferencesPersistence from "hooks/useMapPreferencesPersistence";
import ObjectTableRowActions from "./ObjectTableRowActions";
import ColumnPicker from "components/common/ColumnPicker";
import ObjectsOverviewFilterForm, {
  ObjectsOverviewFilterFormValues
} from "components/forms/objects/ObjectsOverviewFilterForm";

type ObjectsExpandedOverviewTableProps = {
  accountId: string;
  objects: { uniqueId: string }[];
  objectsLoading: boolean;
  totalRecords: number;
  onRowClicked: (row: { uniqueId: string }) => void;
  translator: (key: string) => string;
  showInactiveObjects: boolean;
  objectTypes: Array<{ uniqueId: string; name: string; fields?: any[] }>;
  objectTypesLoading: boolean;
  groups: Array<{ uniqueId: string; name: string }>;
  groupsLoading: boolean;
  resolvedAddresses: any[];
  resolvedAddressLoading: boolean;
  actionsColumn: TableColumn<any>;
};

/**
 * Mounted only on the expanded Objects "Overview" tab so URL/pagination changes
 * from other tabs (e.g. Maintenance Alerts) do not trigger getObjectsByQueryThunk.
 */
const ObjectsExpandedOverviewTable: FC<ObjectsExpandedOverviewTableProps> = ({
  accountId,
  objects,
  objectsLoading,
  totalRecords,
  onRowClicked,
  translator,
  showInactiveObjects,
  objectTypes,
  objectTypesLoading,
  groups,
  groupsLoading,
  resolvedAddresses,
  resolvedAddressLoading,
  actionsColumn
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [overviewFilterOpen, setOverviewFilterOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const { mergeTogglesSync, canPersist } = useMapPreferencesPersistence();

  const { t, i18n } = useTranslation([
    "translation",
    "tableHeadingsTranslation"
  ]);

  const tableColumns = useMemo(
    () =>
      ALL_OBJECTS_TABLE_COLUMNS(
        t,
        i18n.language,
        resolvedAddresses,
        resolvedAddressLoading
      ),
    [t, i18n.language, resolvedAddresses, resolvedAddressLoading]
  );

  const { hiddenIds, visibleColumns, applyHiddenIds, protectedIds } =
    useColumnVisibility({
      storageKey: OBJECTS_COLUMN_VISIBILITY_STORAGE_KEY,
      columns: tableColumns,
      protectedIds: OBJECTS_PROTECTED_COLUMN_IDS
    });

  const columnsWithActions = useMemo(
    () => [...visibleColumns, actionsColumn],
    [visibleColumns, actionsColumn]
  );

  const overviewFilters = useMemo(
    () => parseObjectsFiltersFromSearchParams(searchParams),
    [searchParams]
  );

  const overviewFilterFormInitialValues: ObjectsOverviewFilterFormValues =
    useMemo(
      () => ({
        ...overviewFilters,
        showInactiveObjects
      }),
      [overviewFilters, showInactiveObjects]
    );

  const activeFilterCount = countActiveObjectFilters(overviewFilters);

  const objectsDispatchArgs = useMemo(() => {
    const search = searchParams.get("search") || "";
    const filterWhere = buildObjectsFilterWhereClauses(overviewFilters);
    const statusWhere = buildActiveStatusWhereClauses(showInactiveObjects);

    const where = [
      ...filterWhere,
      ...statusWhere,
      ...(search ? [{ field: "name", value: search, type: 0 }] : [])
    ];

    const commonParams = utils.getCommonParamsForApi();

    return {
      accountId,
      params: {
        ...commonParams,
        where,
        orderBy: commonParams.orderBy?.length
          ? commonParams.orderBy
          : [{ field: "name", ascending: true }]
      }
    };
  }, [accountId, overviewFilters, showInactiveObjects, searchParams]);

  useDispatchOnParams(getObjectsByQueryThunk, {
    args: objectsDispatchArgs
  });

  const handleOverviewFiltersApply = useCallback(
    async (
      values: ObjectsOverviewFilterFormValues,
      formikHelpers: FormikHelpers<ObjectsOverviewFilterFormValues>
    ) => {
      formikHelpers.setSubmitting(true);
      const { showInactiveObjects: nextShowInactive, ...filterValues } = values;

      dispatch(setShowInactiveObjects(nextShowInactive));
      if (canPersist) {
        mergeTogglesSync({ showInactiveObjects: nextShowInactive });
      }

      const params = new URLSearchParams(searchParams);
      applyObjectsFiltersToSearchParams(params, filterValues);
      params.set("page", "1");
      setSearchParams(params, { replace: true });
      formikHelpers.setSubmitting(false);
      setOverviewFilterOpen(false);
    },
    [canPersist, dispatch, mergeTogglesSync, searchParams, setSearchParams]
  );

  const handleExportCsv = useCallback(async () => {
    if (!accountId || exportingCsv) return;

    setExportingCsv(true);
    try {
      const result = await dispatch(
        exportObjectsCsvThunk({
          accountId,
          params: {
            where: objectsDispatchArgs.params.where,
            orderBy: objectsDispatchArgs.params.orderBy
          }
        })
      ).unwrap();

      const contentDisposition = normalizeResponseHeaderValue(
        result?.headers?.["content-disposition"] ??
          result?.headers?.["Content-Disposition"]
      );
      const fileName =
        (contentDisposition &&
          parseFileNameFromContentDisposition(contentDisposition)) ||
        "objects-export.csv";

      const blob =
        result?.data instanceof Blob
          ? result.data
          : new Blob([result?.data], { type: "text/csv" });

      triggerBlobDownload(blob, fileName);
    } catch (error) {
      console.error(error);
      toast.error(
        t("errors.objects.exportFailed", {
          ns: "translation",
          defaultValue: "Failed to export objects CSV. Please try again later."
        })
      );
    } finally {
      setExportingCsv(false);
    }
  }, [accountId, dispatch, objectsDispatchArgs.params, exportingCsv, t]);

  return (
    <ObjectTableBox mx={2}>
      <ReactTable
        columns={columnsWithActions}
        data={objects}
        isSelectable={false}
        isLoading={objectsLoading}
        onRowClicked={onRowClicked}
        paginationTotalRows={totalRecords || 0}
        sx={undefined}
        moduleName="object"
        tableMainBoxStyles={{
          height: "calc(100vh - 314px)",
          border: "1px solid #D5D9E8",
          borderRadius: "10px",
          marginBottom: "10vh",
          padding: "1px"
        }}
        mainTableStyles={{}}
        searchFieldPlaceholder="Search"
        translator={translator}
        headerChildren={
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Badge
              color="primary"
              badgeContent={activeFilterCount || undefined}
              overlap="circular"
            >
              <FilterButton
                id="objects-overview-filter-button"
                theme={theme}
                onClick={() => setOverviewFilterOpen(true)}
                icon={<FilterIcon color={theme.palette.primary.main} />}
                aria-label="Filter objects"
                sx={{
                  "&:hover": {
                    backgroundColor: "rgb(0, 150, 138) !important"
                  }
                }}
              />
            </Badge>
            <Button
              variant="outlined"
              onClick={() => setColumnsOpen(true)}
              startIcon={<ViewColumnOutlined />}
              sx={{
                height: 50,
                borderRadius: "8px",
                textTransform: "none",
                backgroundColor: theme.palette.background.default
              }}
            >
              Columns
            </Button>
            <Button
              id="objects-overview-export-csv"
              variant="outlined"
              onClick={handleExportCsv}
              disabled={!accountId || exportingCsv || objectsLoading}
              startIcon={<FileDownloadOutlined />}
              sx={{
                height: 50,
                borderRadius: "8px",
                textTransform: "none",
                backgroundColor: theme.palette.background.default
              }}
            >
              {t("buttonsText.exportCsv", {
                ns: "translation",
                defaultValue: "Export CSV"
              })}
            </Button>
          </Box>
        }
      />

      <ObjectsOverviewFilterForm
        open={overviewFilterOpen}
        onClose={() => setOverviewFilterOpen(false)}
        initialValues={overviewFilterFormInitialValues}
        onSubmit={handleOverviewFiltersApply}
        objectTypes={objectTypes}
        groups={groups}
        groupsLoading={groupsLoading}
        objectTypesLoading={objectTypesLoading}
      />

      <ColumnPicker
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        columns={tableColumns}
        hiddenIds={hiddenIds}
        protectedIds={protectedIds}
        onApply={(nextHidden) =>
          applyHiddenIds(nextHidden as ObjectsTableColumnId[])
        }
      />
    </ObjectTableBox>
  );
};

const Objects: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Global States
  const { objects, totalRecords, objectsLoading } = useAppSelector(
    selectQueriedObjectsState
  );

  const { resolvedAddresses, resolvedAddressLoading } =
    useObjectsResolvedAddressess(objects);

  const showInactiveObjects = useAppSelector(
    (state) => state.rootReducer.objectsReducer.showInactiveObjects
  );

  const { accountKeys } = useAppSelector(selectAccountKeysState);
  const { objectTypes, loading: objectTypesLoading } = useAppSelector(
    selectObjectTypesState
  );
  const { accountGroups, loading: accountGroupsLoading } =
    useAppSelector(selectAccountGroups);

  const [expandedObjectsTabIndex, setExpandedObjectsTabIndex] = useState(0);

  const [deleteObjectModalOpen, setDeleteObjectModalOpen] = useState(false);
  const [objectPendingDelete, setObjectPendingDelete] = useState<{
    uniqueId: string;
  } | null>(null);
  const [isDeletingObject, setIsDeletingObject] = useState(false);

  const isFetching = useRef(false);

  const drawerSelectedAccountId =
    useAppSelector(selectDrawerSelectedAccountId) || "";

  const { resolvePreferences, canPersist } = useMapPreferencesPersistence();

  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);

  const openRemoveObjectModal = useCallback((object: { uniqueId: string }) => {
    setObjectPendingDelete(object);
    setDeleteObjectModalOpen(true);
  }, []);

  const actionsColumn = useMemo(
    () => ({
      name: t("tableHeading.actions", {
        ns: "tableHeadingsTranslation"
      }),
      width: "72px",
      cell: (row: { uniqueId: string }) => (
        <ObjectTableRowActions
          row={row}
          actionsMenuLabel={t("typography.objectRowActionsAriaLabel")}
          deleteLabel={t("buttonsText.deleteObject")}
          onDelete={openRemoveObjectModal}
        />
      )
    }),
    [t, openRemoveObjectModal]
  );

  const confirmRemoveObject = async () => {
    const objectId = objectPendingDelete?.uniqueId;
    const accountId = drawerSelectedAccountId;
    if (!objectId || !accountId) {
      return;
    }
    setIsDeletingObject(true);
    try {
      await dispatch(
        deleteObjectThunk({
          accountId,
          objectId
        })
      ).unwrap();
      dispatch(removeObjectFromLists(objectId));
      toast.success(t("typography.objectRemoveSuccess"));
      setDeleteObjectModalOpen(false);
      setObjectPendingDelete(null);
    } catch {
      // Error toast handled in thunk
    } finally {
      setIsDeletingObject(false);
    }
  };

  const fetchFilterValues = async () => {
    isFetching.current = true;

    await dispatch(getAllAccountGroupsThunk(drawerSelectedAccountId || ""));
    await dispatch(getObjectTypesByAccountThunk(drawerSelectedAccountId || ""));
    await dispatch(getAllKeysByAccountThunk(drawerSelectedAccountId || ""));

    isFetching.current = false;
  };

  const rowClickHandler = (row: { uniqueId: string }) => {
    dispatch(setObjectActiveTabIndex(0));
    dispatch(setSpecificObject(null));
    dispatch(setActiveObjectId(row?.uniqueId));
    navigate({
      pathname: `/dashboard/objects/details/${row.uniqueId}`
    });
  };

  useEffect(() => {
    if (!isEmpty(drawerSelectedAccountId) && isFetching.current === false) {
      fetchFilterValues();

      if (canPersist) {
        const preferences = resolvePreferences();
        dispatch(setShowInactiveObjects(preferences.showInactiveObjects));
      }
    }
    return () => {
      dispatch(setActiveObjectId(null));
      dispatch(resetAllObjects());
    };
  }, [drawerSelectedAccountId]);

  return (
    <MainWrapperBox>
      <ScrollableBox id="scrollableDiv">
        <Topbar theme={theme} title={t("drawerMenuLinks.objects")} />

        <Box mx={2}>
          <Tabs
            value={expandedObjectsTabIndex}
            onChange={(_, value: number) => setExpandedObjectsTabIndex(value)}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none"
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#845AFC"
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#845AFC"
              }
            }}
          >
            <Tab label={t("maintenanceAlerts.overviewTab")} />
            <Tab label={t("maintenanceAlerts.maintenanceAlertsTab")} />
          </Tabs>
          <Box mt={2}>
            {expandedObjectsTabIndex === 0 ? (
              <ObjectsExpandedOverviewTable
                accountId={drawerSelectedAccountId}
                objects={objects}
                objectsLoading={objectsLoading}
                totalRecords={totalRecords || 0}
                onRowClicked={rowClickHandler}
                translator={t}
                showInactiveObjects={showInactiveObjects}
                objectTypes={objectTypes || []}
                objectTypesLoading={objectTypesLoading}
                groups={accountGroups || []}
                groupsLoading={accountGroupsLoading}
                resolvedAddresses={resolvedAddresses}
                resolvedAddressLoading={resolvedAddressLoading}
                actionsColumn={actionsColumn}
              />
            ) : (
              <AccountMaintenanceAlertsSection
                accountId={drawerSelectedAccountId}
              />
            )}
          </Box>
        </Box>
      </ScrollableBox>

      <DeleteConfirmationModal
        open={deleteObjectModalOpen}
        onClose={() => {
          if (!isDeletingObject) {
            setDeleteObjectModalOpen(false);
            setObjectPendingDelete(null);
          }
        }}
        title={t("typography.deleteObjectTitle")}
        subTitle={t("typography.deleteObjectSubtitle")}
        primaryBtnProps={{
          id: "object-remove-confirm",
          text: t("buttonsText.delete"),
          onClick: confirmRemoveObject,
          loading: isDeletingObject
        }}
        secondaryBtnProps={{
          text: t("buttonsText.cancel")
        }}
        theme={theme}
      />
    </MainWrapperBox>
  );
};

export default withAITracking(reactPlugin, Objects);
